"use client";

import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useStore } from "zustand";

import {
  startExportJob,
  type StartExportJobOptions,
  type StartExportJobResult,
} from "./controller";
import {
  exportJobsStore,
  isBlockingExportJob,
  registerExportReadyHandler,
} from "./store";
import type { ExportReadyHandler, TrackedExportJob } from "./types";

/**
 * Start an export the app-level tracker owns: it runs `post`, polls, and
 * settles the job even after the calling component unmounts.
 */
export function useStartExportJob(): (
  options: StartExportJobOptions,
) => Promise<StartExportJobResult> {
  const queryClient = useQueryClient();
  return useCallback(
    (options) => startExportJob(queryClient, options),
    [queryClient],
  );
}

/**
 * True while a job for `scope` is starting (POST in flight) or polling, until
 * it turns slow and frees the entry point.
 */
export function useExportBusy(scope: string | null): boolean {
  return useStore(
    exportJobsStore,
    (state) =>
      scope !== null &&
      state.jobs.some((job) => job.scope === scope && isBlockingExportJob(job)),
  );
}

/** Every live job, oldest first. */
export function useExportJobs(): readonly TrackedExportJob[] {
  return useStore(exportJobsStore, (state) => state.jobs);
}

/**
 * While mounted, runs as a job for `scope` lands, just before the tracker
 * delivers the file (e.g. to drop the exported selection).
 */
export function useExportReadyHandler(
  scope: string | null,
  handler: ExportReadyHandler,
): void {
  const latest = useRef(handler);
  useLayoutEffect(() => {
    latest.current = handler;
  });
  useEffect(() => {
    if (scope === null) return;
    return registerExportReadyHandler(scope, (job) => latest.current(job));
  }, [scope]);
}
